import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Plate, { Row, RowLabel, RowValue, Divider } from './Plate';
import { COLORS, monoValue } from '../../lib/theme';
import { formatAge, formatAgeMinSec, ageColor } from '../../lib/units';
import { formatCoordinate, parseCoordinate, haversineMeters, formatDistance } from '../../lib/geo';
import { AIS_POSITIONS } from '../../lib/ais';
import { parseWecoTimestamp } from '../../lib/timestamp';

// Top-left of the landscape feed, opposite the telemetry stack. On a phone
// the stack goes static and the video pane grows around it, so this plate
// does the same rather than sit over it.
const Wrap = styled(Plate)`
  top: 16px;
  left: 16px;
  min-width: 216px;
  /* The anchor fields are the one interactive thing over the video, so this
     plate alone opts back into pointer events. */
  pointer-events: auto;

  @media (max-width: 900px) {
    position: static;
    width: 100%;
    max-width: none;
  }
`;

const AnchorRow = styled(Row)`
  gap: 8px;
`;

const Input = styled.input`
  ${monoValue}
  width: 104px;
  text-align: right;
  color: ${(p) => (p.$invalid ? COLORS.alert : COLORS.text)};
  background: rgba(4, 8, 26, 0.9);
  border: 1px solid ${(p) => (p.$invalid ? COLORS.alert : COLORS.hairline)};
  padding: 2px 4px;
  outline: none;

  &:focus {
    /* 2px navy gap, then the yellow ring. */
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 2px ${COLORS.ground}, 0 0 0 4px ${COLORS.accent};
  }
`;

const ResetButton = styled.button`
  ${monoValue}
  font-size: 9px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.label};
  background: none;
  border: 1px solid ${COLORS.hairline};
  padding: 2px 6px;
  cursor: pointer;

  &:hover {
    color: ${COLORS.text};
  }

  &:focus-visible {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 2px ${COLORS.ground}, 0 0 0 4px ${COLORS.accent};
  }
`;

/**
 * Position, the age of that fix, and drift from an operator-set anchor.
 *
 * Coordinates are the last ones actually fixed rather than the current
 * message's -- GPS drops lat/lon entirely while it has no fix, and a known
 * position with its age beside it beats a blank.
 */
export default function PositionPlate({ fields, anchor, onAnchorChange, onAnchorReset, hostname, nowMs }) {
  const { latitude, longitude } = fields;
  const lat = latitude.lastValue;
  const lon = longitude.lastValue;
  const hasFix = lat != null && lon != null;
  const ageMs = latitude.lastValueAgeMs;

  const ais = AIS_POSITIONS[hostname];
  const aisReportedAt = ais ? parseWecoTimestamp(ais.reported) : null;
  const aisAgeMs = aisReportedAt ? nowMs - aisReportedAt.getTime() : null;

  // Drafts so a half-typed coordinate isn't rejected mid-keystroke.
  const [latDraft, setLatDraft] = useState(() => anchor.lat.toFixed(6));
  const [lonDraft, setLonDraft] = useState(() => anchor.lon.toFixed(6));
  const latRef = useRef(null);
  const lonRef = useRef(null);

  useEffect(() => {
    setLatDraft(anchor.lat.toFixed(6));
    setLonDraft(anchor.lon.toFixed(6));
  }, [anchor.lat, anchor.lon]);

  const latParsed = parseCoordinate(latDraft, 'lat');
  const lonParsed = parseCoordinate(lonDraft, 'lon');

  // Reads the inputs rather than the parsed render-time state: pressing Enter
  // straight after the last keystroke can run this handler before React has
  // re-rendered, and a stale closure would then commit the previous value.
  function commit() {
    const nextLat = parseCoordinate(latRef.current?.value ?? '', 'lat');
    const nextLon = parseCoordinate(lonRef.current?.value ?? '', 'lon');
    if (nextLat == null || nextLon == null) return;

    onAnchorChange({ lat: nextLat, lon: nextLon });
  }

  const distance = hasFix ? haversineMeters({ lat, lon }, anchor) : null;

  return (
    <Wrap>
      <Row>
        <RowLabel>LAT</RowLabel>
        <RowValue>{hasFix ? formatCoordinate(lat, 'lat') : '--'}</RowValue>
      </Row>
      <Row>
        <RowLabel>LON</RowLabel>
        <RowValue>{hasFix ? formatCoordinate(lon, 'lon') : '--'}</RowValue>
      </Row>
      <Row>
        <RowLabel>GPS AGE</RowLabel>
        <RowValue $color={ageColor(ageMs)}>{ageMs != null ? formatAgeMinSec(ageMs) : '--'}</RowValue>
      </Row>

      {ais && (
        <>
          <Row>
            <RowLabel>AIS</RowLabel>
            <RowValue>{formatCoordinate(ais.latitude, 'lat')}</RowValue>
          </Row>
          <Row>
            <RowLabel />
            <RowValue>{formatCoordinate(ais.longitude, 'lon')}</RowValue>
          </Row>
          <Row>
            <RowLabel>AIS AGE</RowLabel>
            {fields.aisOn.value === false ? (
              <RowValue $color={COLORS.alert}>OFF</RowValue>
            ) : (
              <RowValue $color={ageColor(aisAgeMs)}>{formatAge(aisAgeMs)}</RowValue>
            )}
          </Row>
        </>
      )}

      <Divider />

      <AnchorRow>
        <RowLabel>ANCHOR</RowLabel>
        <Input
          ref={latRef}
          aria-label="Anchor latitude"
          value={latDraft}
          $invalid={latParsed == null}
          onChange={(e) => setLatDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
        />
      </AnchorRow>
      <AnchorRow>
        <RowLabel />
        <Input
          ref={lonRef}
          aria-label="Anchor longitude"
          value={lonDraft}
          $invalid={lonParsed == null}
          onChange={(e) => setLonDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
        />
      </AnchorRow>
      <AnchorRow>
        <ResetButton type="button" onClick={onAnchorReset}>
          Preset
        </ResetButton>
        <RowValue>{formatDistance(distance)}</RowValue>
      </AnchorRow>
    </Wrap>
  );
}
